import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { IMG } from "@/data/content";

const STATIC_ARTICLES = [
  {
    slug: "cum-alegi-croissant-bun-cafenea",
    title: "Cum alegi un croissant bun pentru cafeneaua ta",
    excerpt: "Stratificarea, untul și coacerea fac diferența între un croissant care vinde și unul pe care îl uiți repede.",
    cover_image: IMG.croissantArtisan,
    category: "Sfaturi pentru patiserii",
    created_at: "2024-11-05T10:00:00Z",
  },
  {
    slug: "produse-congelate-cresc-profit",
    title: "De ce produsele congelate pot crește profitul în HoReCa",
    excerpt: "Mai puține pierderi, mai puțin personal, mai multă constanță. Iată cifrele pe scurt.",
    cover_image: IMG.croissantTray,
    category: "Produse congelate",
    created_at: "2024-10-22T10:00:00Z",
  },
  {
    slug: "blat-pizza-detaliu-schimba-tot",
    title: "Blatul de pizza: detaliul care schimbă tot produsul",
    excerpt: "Aluatul, hidratarea și odihna fac un blat care ține reputația localului.",
    cover_image: IMG.pizzaDough,
    category: "Tendințe bakery",
    created_at: "2024-10-08T10:00:00Z",
  },
  {
    slug: "patiserie-dimineata-vitrina",
    title: "Ce produse de patiserie se vând cel mai bine dimineața",
    excerpt: "Vitrina de cafenea are reguli simple, dar puternice. Iată ce funcționează cel mai bine.",
    cover_image: IMG.trigon,
    category: "Idei pentru vitrină",
    created_at: "2024-09-18T10:00:00Z",
  },
  {
    slug: "reduci-pierderile-bucatarie-horeca",
    title: "Cum reduci pierderile într-o bucătărie HoReCa",
    excerpt: "Pierderile mănâncă marja. Câteva ajustări mici pot schimba complet rezultatul lunar.",
    cover_image: IMG.bakeryAmbient,
    category: "Sfaturi HoReCa",
    created_at: "2024-09-02T10:00:00Z",
  },
];

export default function Blog() {
  const [dynamicArticles, setDynamicArticles] = useState([]);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/articles").then((r) => setDynamicArticles(r.data)).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const articles = [...dynamicArticles, ...STATIC_ARTICLES];

  return (
    <div data-testid="page-blog">
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto container-px text-center">
          <p className="eyebrow">Blog DOTAPAN</p>
          <h1 className="h-display text-4xl md:text-6xl text-text mt-4">Gust, meserie și idei <em className="text-gold not-italic">pentru afacerea ta.</em></h1>
        </div>
      </section>

      {events.length > 0 && (
        <section className="pb-16">
          <div className="max-w-7xl mx-auto container-px">
            <p className="eyebrow mb-6">Noutăți & evenimente</p>
            <div className="grid md:grid-cols-3 gap-5">
              {events.map((ev) => (
                <div key={ev.id} className="card-soft overflow-hidden" data-testid={`event-${ev.id}`}>
                  {ev.image && <img src={ev.image} alt={ev.title} className="w-full h-48 object-cover" />}
                  <div className="p-5">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-gold">{ev.event_date ? new Date(ev.event_date).toLocaleDateString("ro-RO") : "Eveniment"}</p>
                    <h3 className="mt-2 font-display text-lg text-text">{ev.title}</h3>
                    {ev.body && <p className="mt-2 text-sm text-text-soft line-clamp-3">{ev.body}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="pb-24">
        <div className="max-w-7xl mx-auto container-px">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((a, i) => (
              <motion.article
                key={a.slug || a.id}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                className="product-card group flex flex-col h-full"
                data-testid={`article-${a.slug || a.id}`}
              >
                <Link to={`/blog/${a.slug}`} className="flex flex-col h-full">
                  <div className="relative h-56 overflow-hidden">
                    <img src={a.cover_image || IMG.bakeryAmbient} alt={a.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] bg-black/60 text-white border border-white/20">{a.category}</div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-text-soft">
                      <Calendar className="h-3 w-3 text-gold" />
                      {new Date(a.created_at).toLocaleDateString("ro-RO", { year: "numeric", month: "long", day: "numeric" })}
                    </div>
                    <h3 className="mt-3 font-display text-xl text-text">{a.title}</h3>
                    <p className="mt-2 text-sm text-text-soft leading-relaxed flex-1">{a.excerpt}</p>
                    <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold">
                      Citește <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export { STATIC_ARTICLES };
